import type { T_Maybe } from '@tryiris-ai/js-types';
import { isElectronApp } from '#~/electron/utils.js';

/**
 * Where an embedded-device linking flow is running (ENG-3960 §A.5 embedded flow).
 *
 * - `electron`: the desktop Electron app, where embedded (Iris-firmware) cameras
 *   are discovered over mDNS and linked locally.
 * - `webfig`: the browser build of the studio client.
 *
 * Per rule: investigate-before-adding — `linking_surface` is an existing canonical
 * prop (analytics/types/properties.ts CommonProperties) already emitted by the
 * discovery flow; this only narrows the embedded-flow values to a typed union.
 */
export type LinkingSurface = 'electron' | 'webfig';

/**
 * Resolve the linking surface for the current runtime. Kept in its own module
 * (separate from the pure tracker) so the tracker can be unit-tested without
 * loading the Electron environment probe in `#~/electron/utils.js`.
 *
 * Per rule: code-comments-required — WHY: the same embedded flow ships in both
 * the Electron app and the web build, and adoption must be attributable to the
 * surface that actually fired the event.
 */
export const resolveLinkingSurface = (): LinkingSurface =>
  isElectronApp() ? 'electron' : 'webfig';

/**
 * Classify an in-flight discovery as embedded, mirroring the §1.6 gate in the
 * discovery selector table: an "Iris" connection — or an iris-enabled row — is an
 * embedded (Iris-firmware) device.
 *
 * Per rule: investigate-before-adding — single source of truth so wizard steps 2-8
 * classify the in-flight flow identically to the step-1 device_click_discovered emit
 * (selector/table.tsx), rather than re-deriving the predicate at each call site.
 */
export const isEmbeddedDiscovery = (
  discovery: T_Maybe<{ connection?: string; irisEnabled?: boolean }>
): boolean => discovery?.connection === 'Iris' || Boolean(discovery?.irisEnabled);

/** Additive embedded-flow cohort props, or `{}` when the flow is not embedded. */
export type EmbeddedFlowProps =
  | { is_embedded: true; linking_surface: LinkingSurface }
  | Record<string, never>;

/**
 * Additive embedded-flow enrichment for a shared Add Device wizard step emit.
 *
 * The embedded flow has no parallel UI — it reuses the shared wizard (§1.6), so
 * steps 2-8 fire the SAME canonical events as the generic path. This returns the
 * additive is_embedded + linking_surface cohort to spread into the EXISTING
 * track() call when the flow is embedded, and `{}` otherwise — so no second event
 * is minted (no double-fire) and the generic path stays byte-for-byte unchanged.
 *
 * Per rule: code-comments-required — WHY: the Electron embedded path reuses these
 * same wizard emits, so resolveLinkingSurface stamps linking_surface:'electron'
 * there automatically with no separate Electron event surface to maintain.
 */
export const resolveEmbeddedFlowProps = (isEmbedded: boolean): EmbeddedFlowProps =>
  isEmbedded ? { is_embedded: true, linking_surface: resolveLinkingSurface() } : {};
